import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, CheckCircle2, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Reveal, Eyebrow } from "./Motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Kitchen Remodel", "Bathroom Remodel", "Deck / Patio", "Flooring & Tile", "Painting", "Repairs & Carpentry", "Other"];
const TIMELINES = ["As soon as possible", "Within 1–3 months", "3–6 months", "Just planning"];
const empty = { name: "", email: "", phone: "", service: "", timeline: "", message: "" };
const field = "h-12 rounded-xl border-[#D6CFC4] bg-white/70 px-4 text-base focus-visible:ring-[#C85A32] focus-visible:ring-offset-0";

const Label = ({ children, htmlFor }) => (
  <label htmlFor={htmlFor} className="mb-2 block font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#605A52]">{children}</label>
);

export const QuoteForm = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.service) return toast.error("Please choose a service.");
    if (form.message.trim().length < 10) return toast.error("Tell us a little more about the project (10+ characters).");
    setLoading(true);
    try {
      await axios.post(`${API}/quotes`, { ...form, timeline: form.timeline || null });
      setDone(true);
      setForm(empty);
      toast.success("Request received — we'll reach out shortly.");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Please check your details and try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="quote" className="relative py-24 lg:py-36" data-testid="quote-section">
      <div className="mx-auto grid max-w-[1400px] gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:px-12">
        <div className="lg:col-span-5">
          <Reveal>
            <Eyebrow>Start your project</Eyebrow>
            <h2 className="mt-5 font-serif-d text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight leading-[0.95]">
              Let's talk about <span className="italic text-[#B04E2A]">your home.</span>
            </h2>
            <p className="mt-6 max-w-md text-base md:text-lg text-[#605A52] leading-relaxed">
              Tell us what you have in mind. We'll get back to you to set up a walkthrough and a clear, itemized quote.
            </p>
            <a href="tel:+13522587381" data-testid="quote-call-link"
              className="mt-6 inline-flex items-center gap-3 font-serif-d text-3xl font-semibold text-[#1E1B18] transition-colors hover:text-[#B04E2A]">
              <Phone className="h-5 w-5 text-[#B04E2A]" /> (352) 258-7381
            </a>
            <p className="mt-2 font-mono-d text-[11px] uppercase tracking-[0.2em] text-[#605A52]">Serving all of North &amp; Central Florida</p>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="mt-10 overflow-hidden rounded-[20px] roof-clip aspect-[5/4] hidden sm:block">
              <img src="/img/kitchen2.jpg" alt="Kitchen island detail with wooden stools" loading="lazy" className="h-full w-full object-cover" />
            </div>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-7" delay={0.1}>
          {done ? (
            <div className="flex h-full min-h-[420px] flex-col items-start justify-center rounded-[24px] border border-[#E2DDD5] bg-white/60 p-8 sm:p-12" data-testid="quote-success">
              <CheckCircle2 className="h-12 w-12 text-[#4A584E]" />
              <h3 className="mt-6 font-serif-d text-4xl font-semibold">Thank you — request received.</h3>
              <p className="mt-3 text-[#605A52]">We'll be in touch soon to talk through your project.</p>
              <button onClick={() => setDone(false)} data-testid="quote-send-another-button" className="mt-8 text-sm font-semibold underline underline-offset-4 hover:text-[#B04E2A]">Send another request</button>
            </div>
          ) : (
            <form onSubmit={submit} className="grid gap-6 rounded-[24px] border border-[#E2DDD5] bg-white/60 p-6 sm:p-10 sm:grid-cols-2" data-testid="quote-form">
              <div><Label htmlFor="q-name">Full name</Label>
                <Input id="q-name" required minLength={2} value={form.name} onChange={set("name")} className={field} data-testid="quote-form-name-input" /></div>
              <div><Label htmlFor="q-phone">Phone</Label>
                <Input id="q-phone" type="tel" required minLength={7} value={form.phone} onChange={set("phone")} className={field} data-testid="quote-form-phone-input" /></div>
              <div className="sm:col-span-2"><Label htmlFor="q-email">Email</Label>
                <Input id="q-email" type="email" required value={form.email} onChange={set("email")} className={field} data-testid="quote-form-email-input" /></div>
              <div><Label>Service</Label>
                <Select value={form.service} onValueChange={set("service")}>
                  <SelectTrigger className={field} data-testid="quote-form-service-select"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                  <SelectContent>{SERVICES.map((s) => <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><Label>Timeline</Label>
                <Select value={form.timeline} onValueChange={set("timeline")}>
                  <SelectTrigger className={field} data-testid="quote-form-timeline-select"><SelectValue placeholder="When to start?" /></SelectTrigger>
                  <SelectContent>{TIMELINES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="sm:col-span-2"><Label htmlFor="q-msg">Project details</Label>
                <Textarea id="q-msg" required rows={5} value={form.message} onChange={set("message")} placeholder="Rooms, size, ideas, anything we should know…"
                  className="rounded-xl border-[#D6CFC4] bg-white/70 p-4 text-base focus-visible:ring-[#C85A32] focus-visible:ring-offset-0" data-testid="quote-form-message-textarea" /></div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={loading} data-testid="quote-form-submit-button"
                  className="group inline-flex items-center gap-3 rounded-full bg-[#1E1B18] pl-7 pr-2 py-2 text-sm font-semibold text-[#F8F6F0] transition-colors duration-300 hover:bg-[#C85A32] disabled:opacity-60">
                  {loading ? "Sending…" : "Send My Request"}
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-white/15 transition-transform duration-500 group-hover:rotate-45">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUpRight className="h-4 w-4" />}
                  </span>
                </button>
              </div>
            </form>
          )}
        </Reveal>
      </div>
    </section>
  );
};
